/**
 * DV-DEP-001: YouTube Integration Card Component
 *
 * Component that displays YouTube account connection status and provides
 * connect/disconnect functionality for YouTube integration.
 */

import React from 'react';
import { Youtube, Check, AlertCircle, ExternalLink, Unlink } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useYoutubeIntegration, useInitiateYoutubeOAuth, useDisconnectYoutube } from '@/hooks/useDeployments';

const YoutubeIntegrationCard: React.FC = () => {
  const {
    data: integrationData,
    isLoading,
    isError,
    error
  } = useYoutubeIntegration();

  const initiateOAuth = useInitiateYoutubeOAuth();
  const disconnectYoutube = useDisconnectYoutube();

  const integration = integrationData?.integration;
  const isConnected = integration?.isConnected ?? false;

  const handleConnect = async () => {
    try {
      const result = await initiateOAuth.mutateAsync();
      if (result.authUrl) {
        // Redirect to OAuth URL
        window.location.href = result.authUrl;
      }
    } catch (err) {
      console.error('Failed to initiate YouTube OAuth:', err);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect your YouTube account? This will cancel any pending uploads.')) {
      return;
    }

    try {
      await disconnectYoutube.mutateAsync();
    } catch (err) {
      console.error('Failed to disconnect YouTube:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-black/40 backdrop-blur-sm border border-cyan-500/10 rounded-lg p-6">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-white/10 rounded-lg animate-pulse" />
          <div className="flex-1">
            <div className="h-4 bg-white/10 rounded animate-pulse mb-2" />
            <div className="h-3 bg-white/10 rounded animate-pulse w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
        <div className="flex items-center space-x-3">
          <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
          <div>
            <h3 className="text-red-400 font-medium">
              Failed to load YouTube integration
            </h3>
            <p className="text-red-300/60 text-sm mt-1">
              {error?.message || 'Unknown error occurred'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      'bg-black/40 backdrop-blur-sm border rounded-lg p-6 transition-colors',
      isConnected
        ? 'border-green-500/20 bg-green-500/5'
        : 'border-cyan-500/10'
    )}>
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          {/* YouTube Icon */}
          <div className={cn(
            'w-12 h-12 rounded-lg flex items-center justify-center',
            isConnected
              ? 'bg-red-500/20 border border-red-500/30'
              : 'bg-white/10 border border-white/20'
          )}>
            <Youtube className={cn(
              'w-6 h-6',
              isConnected ? 'text-red-400' : 'text-white/60'
            )} />
          </div>

          {/* Connection Info */}
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-lg font-medium text-white">
                YouTube Integration
              </h3>
              {isConnected && (
                <div className="flex items-center space-x-1 text-green-400 text-sm">
                  <Check className="w-4 h-4" />
                  <span className="font-mono">Connected</span>
                </div>
              )}
            </div>

            {isConnected && integration ? (
              <div className="space-y-2">
                <div className="flex items-center space-x-3">
                  {integration.channelThumbnail && (
                    <img
                      src={integration.channelThumbnail}
                      alt={integration.channelTitle}
                      className="w-8 h-8 rounded-full border border-white/20"
                    />
                  )}
                  <div>
                    <p className="text-white font-medium">
                      {integration.channelTitle}
                    </p>
                    <p className="text-white/60 text-sm font-mono">
                      Connected {new Date(integration.connectedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {integration.lastUsedAt && (
                  <p className="text-white/40 text-xs font-mono">
                    Last used: {new Date(integration.lastUsedAt).toLocaleString()}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-white/60 text-sm">
                Connect your YouTube account to upload videos directly from HI-SI
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-3">
          {isConnected ? (
            <button
              onClick={handleDisconnect}
              disabled={disconnectYoutube.isPending}
              className={cn(
                'px-4 py-2 border rounded-lg font-mono text-sm transition-colors',
                'flex items-center space-x-2',
                disconnectYoutube.isPending
                  ? 'bg-gray-500/20 border-gray-500/30 text-gray-400 cursor-not-allowed'
                  : 'bg-red-500/20 border-red-500/30 text-red-400 hover:bg-red-500/30'
              )}
            >
              <Unlink className="w-4 h-4" />
              <span>
                {disconnectYoutube.isPending ? 'Disconnecting...' : 'Disconnect'}
              </span>
            </button>
          ) : (
            <button
              onClick={handleConnect}
              disabled={initiateOAuth.isPending}
              className={cn(
                'px-4 py-2 border rounded-lg font-mono text-sm transition-colors',
                'flex items-center space-x-2',
                initiateOAuth.isPending
                  ? 'bg-gray-500/20 border-gray-500/30 text-gray-400 cursor-not-allowed'
                  : 'bg-cyan-500/20 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/30'
              )}
            >
              <ExternalLink className="w-4 h-4" />
              <span>
                {initiateOAuth.isPending ? 'Connecting...' : 'Connect YouTube'}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Connection Benefits/Info */}
      {!isConnected && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start space-x-2">
              <Check className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white/80">Direct Upload</p>
                <p className="text-white/50 text-xs">Upload videos directly to YouTube</p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <Check className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white/80">Scheduled Publishing</p>
                <p className="text-white/50 text-xs">Schedule videos for future release</p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <Check className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white/80">Metadata Control</p>
                <p className="text-white/50 text-xs">Set titles, descriptions, and tags</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {(initiateOAuth.isError || disconnectYoutube.isError) && (
        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400 text-sm font-mono">
            {initiateOAuth.error?.message || disconnectYoutube.error?.message || 'An error occurred'}
          </p>
        </div>
      )}
    </div>
  );
};

export default YoutubeIntegrationCard;